const MOBILE_QUERY = window.matchMedia('(max-width: 991px)');

class DotoMobileHomeNav {
  constructor(shell) {
    this.shell = shell;
    this.bottomNav = shell.querySelector('[data-doto-mobile-bottom-nav]');
    this.drawer = shell.querySelector('[data-doto-drawer]');
    this.categoriesButton = shell.querySelector('[data-doto-bottom-categories]');
    this.categoriesSheet = shell.querySelector('[data-doto-home-categories-sheet]');
    this.categoriesClose = shell.querySelector('[data-doto-home-categories-close]');
    this.gorgiasLauncher = null;
    this.domObserver = null;
    this.boundMedia = () => this.syncMode();
    this.boundGorgiasLoaded = () => this.syncGorgias();

    this.onCategoriesClick = () => this.openCategories();
    this.onCategoriesClose = () => this.closeCategories();
    this.onCategoriesSheetClick = (event) => {
      if (event.target === this.categoriesSheet) this.closeCategories();
    };
    this.onCategoriesSheetClosed = () => this.syncCategoriesClosedState();

    this.categoryDrag = {
      active: false,
      startY: 0,
      currentY: 0,
      startedAt: 0,
      canStartFromList: false
    };

    this.onCategoryTouchStart = (event) => this.startCategoryDrag(event);
    this.onCategoryTouchMove = (event) => this.moveCategoryDrag(event);
    this.onCategoryTouchEnd = () => this.endCategoryDrag();

    this.bind();
    this.syncMode();
  }

  bind() {
    this.categoriesButton?.addEventListener('click', this.onCategoriesClick);
    this.categoriesClose?.addEventListener('click', this.onCategoriesClose);
    this.categoriesSheet?.addEventListener('click', this.onCategoriesSheetClick);
    this.categoriesSheet?.addEventListener('close', this.onCategoriesSheetClosed);
    this.categoriesSheet?.addEventListener('touchstart', this.onCategoryTouchStart, { passive: true });
    this.categoriesSheet?.addEventListener('touchmove', this.onCategoryTouchMove, { passive: false });
    this.categoriesSheet?.addEventListener('touchend', this.onCategoryTouchEnd, { passive: true });
    this.categoriesSheet?.addEventListener('touchcancel', this.onCategoryTouchEnd, { passive: true });
    MOBILE_QUERY.addEventListener?.('change', this.boundMedia);
    window.addEventListener('gorgias-widget-loaded', this.boundGorgiasLoaded);
  }

  syncMode() {
    if (MOBILE_QUERY.matches) {
      document.body.classList.add('doto-home-bottom-nav-active');
      this.setupFloatingTools();
    } else {
      document.body.classList.remove('doto-home-bottom-nav-active');
      this.closeCategories();
      this.teardownFloatingTools();
    }
  }

  openCategories() {
    if (!this.categoriesSheet?.showModal) return;

    this.categoriesSheet.querySelectorAll('[data-doto-category-accordion]').forEach((group) => {
      group.classList.remove('is-open');
      const button = group.querySelector('[data-doto-category-toggle]');
      const panel = group.querySelector('[data-doto-category-panel]');
      button?.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });

    this.resetCategoryDrag();
    if (!this.categoriesSheet.open) this.categoriesSheet.showModal();
    this.categoriesButton?.setAttribute('aria-expanded', 'true');
    this.categoriesButton?.classList.add('is-active');
    document.documentElement.classList.add('doto-home-categories-lock');
  }

  closeCategories() {
    if (this.categoriesSheet?.open) this.categoriesSheet.close();
    else this.syncCategoriesClosedState();
  }

  syncCategoriesClosedState() {
    this.categoriesButton?.setAttribute('aria-expanded', 'false');
    this.categoriesButton?.classList.remove('is-active');
    document.documentElement.classList.remove('doto-home-categories-lock');
  }


  resetCategoryDrag() {
    if (!this.categoriesSheet) return;
    this.categoriesSheet.classList.remove('is-dragging');
    this.categoriesSheet.style.removeProperty('--doto-category-sheet-drag-y');
    this.categoryDrag.active = false;
    this.categoryDrag.startY = 0;
    this.categoryDrag.currentY = 0;
    this.categoryDrag.startedAt = 0;
    this.categoryDrag.canStartFromList = false;
  }

  startCategoryDrag(event) {
    if (!MOBILE_QUERY.matches || !this.categoriesSheet?.open || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const target = event.target;
    const list = this.categoriesSheet.querySelector('.doto-home-categories-sheet__list');
    const startedOnHandle = Boolean(target.closest('.doto-home-categories-sheet__handle, .doto-home-categories-sheet__head'));
    const startedInsideList = Boolean(target.closest('.doto-home-categories-sheet__list'));
    const listAtTop = !list || list.scrollTop <= 1;

    this.categoryDrag.active = startedOnHandle || (startedInsideList && listAtTop);
    this.categoryDrag.canStartFromList = startedInsideList && listAtTop;
    this.categoryDrag.startY = touch.clientY;
    this.categoryDrag.currentY = touch.clientY;
    this.categoryDrag.startedAt = performance.now();
  }

  moveCategoryDrag(event) {
    if (!this.categoryDrag.active || event.touches.length !== 1 || !this.categoriesSheet) return;

    const touch = event.touches[0];
    const deltaY = touch.clientY - this.categoryDrag.startY;
    const list = this.categoriesSheet.querySelector('.doto-home-categories-sheet__list');

    if (deltaY <= 0) {
      if (this.categoryDrag.canStartFromList) this.categoryDrag.active = false;
      return;
    }

    if (this.categoryDrag.canStartFromList && list && list.scrollTop > 1) {
      this.categoryDrag.active = false;
      return;
    }

    if (deltaY < 5) return;

    event.preventDefault();
    this.categoryDrag.currentY = touch.clientY;
    this.categoriesSheet.classList.add('is-dragging');

    // Mild resistance prevents the sheet from feeling detached from the viewport.
    const translated = Math.min(deltaY * 0.88, window.innerHeight * 0.72);
    this.categoriesSheet.style.setProperty('--doto-category-sheet-drag-y', `${translated}px`);
  }

  endCategoryDrag() {
    if (!this.categoriesSheet) return;

    const deltaY = this.categoryDrag.currentY - this.categoryDrag.startY;
    const elapsed = Math.max(performance.now() - this.categoryDrag.startedAt, 1);
    const velocity = deltaY / elapsed;
    const shouldClose = this.categoryDrag.active && (deltaY >= 90 || velocity >= 0.55);

    this.categoriesSheet.classList.remove('is-dragging');

    if (shouldClose) {
      const panel = this.categoriesSheet.querySelector('.doto-home-categories-sheet__panel');
      const distance = panel?.offsetHeight || window.innerHeight;
      this.categoriesSheet.style.setProperty('--doto-category-sheet-drag-y', `${distance}px`);
      window.setTimeout(() => {
        this.closeCategories();
        this.resetCategoryDrag();
      }, 170);
      return;
    }

    this.categoriesSheet.style.setProperty('--doto-category-sheet-drag-y', '0px');
    window.setTimeout(() => this.resetCategoryDrag(), 230);
  }

  setupFloatingTools() {
    if (this.domObserver) {
      this.scanWhatsApp();
      this.syncGorgias();
      return;
    }

    this.scanWhatsApp();
    this.syncGorgias();

    this.domObserver = new MutationObserver(() => {
      this.scanWhatsApp();
      this.syncGorgias();
    });
    this.domObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  teardownFloatingTools() {
    this.domObserver?.disconnect();
    this.domObserver = null;
    document.querySelectorAll('[data-doto-home-whatsapp]').forEach((node) => {
      node.style.removeProperty('bottom');
      node.removeAttribute('data-doto-home-whatsapp');
    });
    this.gorgiasLauncher?.remove();
    this.gorgiasLauncher = null;
    this.restoreNativeGorgias();
  }

  findFixedAncestor(node) {
    let current = node;
    for (let i = 0; current && i < 8; i += 1, current = current.parentElement) {
      const style = window.getComputedStyle(current);
      if (style.position === 'fixed') return current;
    }
    return node;
  }

  scanWhatsApp() {
    if (!MOBILE_QUERY.matches) return;
    const selector = [
      'a[href*="wa.me"]',
      'a[href*="whatsapp.com"]',
      'a[href*="api.whatsapp.com"]',
      '[aria-label*="whatsapp" i]',
      '[title*="whatsapp" i]',
      '[class*="whatsapp" i]',
      '[id*="whatsapp" i]'
    ].join(',');

    document.querySelectorAll(selector).forEach((candidate) => {
      const root = this.findFixedAncestor(candidate);
      root?.setAttribute('data-doto-home-whatsapp', '');
    });
  }

  syncGorgias() {
    if (!MOBILE_QUERY.matches) return;
    this.hideNativeGorgias();
    if (this.gorgiasLauncher?.isConnected) return;

    const create = () => {
      if (!MOBILE_QUERY.matches || this.gorgiasLauncher?.isConnected) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'doto-home-gorgias-launcher';
      button.setAttribute('aria-label', 'Abrir chat de ayuda');
      button.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
          <path d="M4.5 4.75h15A2.75 2.75 0 0 1 22.25 7.5v7A2.75 2.75 0 0 1 19.5 17.25H10l-5.75 3v-3.82A2.75 2.75 0 0 1 1.75 13.7V7.5A2.75 2.75 0 0 1 4.5 4.75Z" fill="currentColor"/>
          <circle cx="7.6" cy="11" r="1.15" fill="white"/>
          <circle cx="12" cy="11" r="1.15" fill="white"/>
          <circle cx="16.4" cy="11" r="1.15" fill="white"/>
        </svg>`;
      button.addEventListener('click', () => {
        if (window.GorgiasChat?.open) window.GorgiasChat.open();
      });
      document.body.appendChild(button);
      this.gorgiasLauncher = button;
    };

    if (window.GorgiasChat) {
      Promise.resolve(window.GorgiasChat.init?.()).finally(create);
    } else {
      create();
    }
  }

  hideNativeGorgias() {
    document.querySelectorAll('#gorgias-chat-container iframe#chat-button, iframe#chat-button').forEach((launcher) => {
      launcher.setAttribute('data-doto-home-gorgias-native', '');
      launcher.style.setProperty('display', 'none', 'important');
      launcher.style.setProperty('visibility', 'hidden', 'important');
      launcher.style.setProperty('pointer-events', 'none', 'important');
    });
  }

  restoreNativeGorgias() {
    document.querySelectorAll('[data-doto-home-gorgias-native]').forEach((launcher) => {
      launcher.style.removeProperty('display');
      launcher.style.removeProperty('visibility');
      launcher.style.removeProperty('pointer-events');
      launcher.removeAttribute('data-doto-home-gorgias-native');
    });
  }

  destroy() {
    this.categoriesButton?.removeEventListener('click', this.onCategoriesClick);
    this.categoriesClose?.removeEventListener('click', this.onCategoriesClose);
    this.categoriesSheet?.removeEventListener('click', this.onCategoriesSheetClick);
    this.categoriesSheet?.removeEventListener('close', this.onCategoriesSheetClosed);
    this.categoriesSheet?.removeEventListener('touchstart', this.onCategoryTouchStart);
    this.categoriesSheet?.removeEventListener('touchmove', this.onCategoryTouchMove);
    this.categoriesSheet?.removeEventListener('touchend', this.onCategoryTouchEnd);
    this.categoriesSheet?.removeEventListener('touchcancel', this.onCategoryTouchEnd);
    MOBILE_QUERY.removeEventListener?.('change', this.boundMedia);
    window.removeEventListener('gorgias-widget-loaded', this.boundGorgiasLoaded);
    document.body.classList.remove('doto-home-bottom-nav-active');
    this.syncCategoriesClosedState();
    this.teardownFloatingTools();
  }
}

const instances = new Map();

function init(shell) {
  if (!shell || instances.has(shell) || !shell.querySelector('[data-doto-mobile-bottom-nav]')) return;
  instances.set(shell, new DotoMobileHomeNav(shell));
}

function destroy(shell) {
  instances.get(shell)?.destroy();
  instances.delete(shell);
}

document.querySelectorAll('[data-doto-site-header-shell]').forEach(init);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll?.('[data-doto-site-header-shell]').forEach(init);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll?.('[data-doto-site-header-shell]').forEach(destroy);
});
